import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader, Crown, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_LABELS = {
  landlord_pro_monthly:    { label: 'RentalX Pro — Monthly',              icon: Building2, color: '#094f6f' },
  landlord_pro_annual:     { label: 'RentalX Pro — Annual',               icon: Building2, color: '#094f6f' },
  vibemate_plus:           { label: 'VibeMate Plus — Monthly',            icon: Crown,     color: '#2cbdc4' },
  founding_student:        { label: 'Founding Student — Monthly',         icon: Crown,     color: '#2cbdc4' },
  founding_professional:   { label: 'Founding Professional — Monthly',    icon: Crown,     color: '#094f6f' },
  founding_landlord:       { label: 'Founding Property Manager — Monthly', icon: Building2, color: '#094f6f' },
};

export default function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const plan      = searchParams.get('plan') || '';
  const [status, setStatus] = useState('loading'); // loading | success | failed
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus('failed'); return; }
    poll(0);
  }, [sessionId]);

  const poll = async (n) => {
    if (n >= 8) { setStatus('failed'); return; }
    try {
      const r = await fetch(`${API}/api/payments/stripe/status/${sessionId}`);
      const d = await r.json();
      if (d.payment_status === 'paid') setStatus('success');
      else if (d.status === 'expired') setStatus('failed');
      else { setAttempts(n + 1); setTimeout(() => poll(n + 1), 2500); }
    } catch { setTimeout(() => poll(n + 1), 2500); }
  };

  const meta = PLAN_LABELS[plan] || { label: 'Your subscription', icon: CheckCircle, color: '#094f6f' };
  const Icon = meta.icon;
  const dashboardUrl = plan.startsWith('vibemate') ? '/vibemate/app'
    : plan.startsWith('founding') ? '/founding'
    : '/landlord-dashboard';

  if (status === 'loading') return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <Loader className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: meta.color }} />
        <p className="text-gray-600 font-medium">Activating your subscription…</p>
        <p className="text-sm text-gray-400 mt-1">Please wait, do not close this page</p>
      </div>
    </div>
  );

  if (status === 'failed') return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center bg-white rounded-3xl shadow-xl p-10">
        <div className="text-5xl mb-4">❌</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-3">Payment not completed</h2>
        <p className="text-gray-500 text-sm mb-6">No charge was made. Please try again.</p>
        <Link to="/pricing" className="inline-block text-white px-6 py-3 rounded-xl font-semibold text-sm"
          style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
          Back to Pricing
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-green-100 overflow-hidden">
        <div className="h-2 w-full" style={{ background: 'linear-gradient(90deg,#2cbdc4,#094f6f)' }} />
        <div className="p-10 text-center">
          {/* Logo */}
          <Link to="/" className="flex items-center justify-center gap-0 mb-8">
            <span className="text-2xl font-black tracking-tight" style={{ color: '#094f6f' }}>Rental</span>
            <img src="/rentalx-x-new.svg" alt="X"
              style={{ height: '1.7rem', width: 'auto', marginLeft: '0.05em', marginBottom: '0.02em', verticalAlign: 'middle' }} />
          </Link>

          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-gray-900 mb-2">You're all set! 🎉</h2>
          <p className="text-gray-500 text-sm mb-6">
            <strong>{meta.label}</strong> is now active on your account.
          </p>

          <div className="bg-gray-50 rounded-2xl p-5 mb-6 text-left space-y-2.5">
            {[
              plan === 'landlord_pro_monthly'  && ['Billing', 'A$24.00 / month'],
              plan === 'landlord_pro_annual'   && ['Billing', 'A$228.00 / year (A$19/mo)'],
              plan === 'vibemate_plus'          && ['Billing', 'A$9.99 / month'],
              ['Next renewal', plan.includes('annual') ? 'In 12 months' : 'In 30 days'],
              ['Invoice', 'Sent to your registered email'],
              ['Cancel anytime', 'From your dashboard settings'],
            ].filter(Boolean).map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm">
                <span className="text-gray-500">{k}</span>
                <span className="font-medium text-gray-900">{v}</span>
              </div>
            ))}
          </div>

          <div className="space-y-2.5">
            <Link to={dashboardUrl}
              className="block w-full text-center text-white py-3.5 rounded-xl font-bold text-sm"
              style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
              Go to Dashboard →
            </Link>
            <Link to="/" className="block w-full text-center text-gray-400 text-sm py-2 hover:text-gray-600">
              Back to Homepage
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
